import { EMPTY_ARRAY } from './constants'

/**
 * Creates a regex depending of the text and options parameters
 * @param   {string}           pattern - text to create the regex
 * @param   {string}           [flags='gi'] - regex options
 *
 * @returns {(object|boolean)} a regex with the pattern and flags received if are valid, else returns false
*/
export const regexCreator = (pattern, flags = 'gi') => {
  try {
    return new RegExp(`${pattern}`, flags)
  } catch (e) {
    return false
  }
}

/**
 * Compare both parameters taking care of case sensitive, formatting the output string and returning it
 * @param   {array}  matches - collection of coincidences of the typed query with the item to filter
 * @param   {string} name - name to filter
 *
 * @returns {string} the name formatted with <em> tags where the matchKey coincided with a key from the name
*/
export const getFilteredName = (matches, name) => {
  if (matches) {
    return [...new Set(matches)].reduce((acc, curr) => {
      const matchKey = regexCreator(`${curr}(?!m>)`, 'g')
      acc = acc.replace(matchKey, `<em>${curr}</em>`)
      return acc
    }, name)
  }
}

/**
 * Takes a query to filter through an array of objects by name
 * @param   {string} query - search input
 * @param   {array}  arr - array of objects to filter
 *
 * @returns {array}  an array of object filtered by query parameter
 */
export const filterizer = query => arr => {
  if (query.includes('.')) {
    return []
  } else if (query) {
    const regex = regexCreator(query)
    return arr.filter(({ name }) => name && name.match(regex))
      .map(item => {
        const matches = item.name.match(regex)
        const filteredName = getFilteredName(matches, item.name)
        return { ...item, name: item.name, filteredName }
      })
  }
  return arr
}

/**
 * Sort an array of objects depending of parameters received
 * @param   {array}   arr - array of objects to sort
 * @param   {string}  field - string key that will be used to order
 * @param   {boolean} descending - if true is decending order
 * @param   {boolean} stringNumbers - if true should be able to order strings that can coerse to number
 *
 * @returns {array}   an array of objects sorted descendingly or ascendingly
*/
export const sortingBy = (arr = EMPTY_ARRAY, field, descending = false, stringNumbers = false) => {
  let arrayToSort = [...arr]
  if (stringNumbers && descending) {
    arrayToSort = arrayToSort.map(s => ({ ...s, name: +s.name }))
  }
  if (descending) {
    return arrayToSort.sort((a, b) => a[field] < b[field] ? 1 : -1)
  } else {
    return arrayToSort.sort((a, b) => a[field].toLowerCase() > b[field].toLowerCase() ? 1 : -1)
  }
}

/**
 * Combine functions from left to right, passing the return value as input for the next function
 * @param  {...function} functions -  functions in execute on a value
 * @param {any} input - intitial value that will be transformed
 *
 * @returns {any} the initial input transformed by all the functions it's passed through
 */
export const pipe = (...functions) => input => functions.reduce((response, func) => func(response), input)

export const asyncPipe = (...functions) => input => functions.reduce((response, func) => response.then(func), Promise.resolve(input))

export const debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, timeout)
  }
}
